import { tiers as Tiers } from "venuescanner-js/constants/tiers-es";
import { Geo } from "venuescanner-js/vanilla/components/geo";
import { authenticationService } from "venuescanner-js/services/auth/service";
import isSSR from "venuescanner-js/services/isSSR";

import { postToRails } from "venuescanner-js/services/api";
const getResultsCache = {};
const locationCache = {};
const parentCache = {};
const inFlight = {};
const cityKinds = [
  "city",
  "City",
  "Locality",
  "Village",
  "Hamlet",
  "Suburban Area",
  "region",
  "district",
  "Town",
  "county",
  "country",
];
const controller =
  typeof AbortController !== "undefined" ? new AbortController() : null;

let keyPlaces = [];
const Place = function (data) {
  if (data.location) {
    Object.keys(data.location).forEach((k) => {
      this[k] = data.location[k];
    });
    this.id = this.id.toString();
  } else {
    this.id = data.id.toString();
    this.name = data.n;
    this.title = data.t;
    this.kind = data.k;
    this.tier = data.tr;
    this.zoom = data.z;
    this.contry_code = data.cc;
    this.slug = data.sg;
    this.citySlug = data.c_sg;
    this.lat = data.lt;
    this.lng = data.lg;
    this.radius = data.r * 1;
  }
  this.placeData = {
    id: this.id,
    name: this.name,
    lat: this.lat,
    lng: this.lng,
  };
  locationCache[this.id] = this;
};
export const locations = {
  cachedLocationBySlug: function (slug) {
    return Object.values(locationCache).find((l) => l.slug === slug);
  },
  fetchLocations: async function (
    query = "",
    { keyplaces = false } = { keyplaces: false }
  ) {
    try {
      // console.log(await postToRails('places', { query, keyplaces: keyplaces || !query }, { returnData: false }))
      const { data = [], key_places = [] } =
        !query && keyPlaces.length
          ? { data: [], key_places: keyPlaces }
          : typeof window !== "undefined"
          ? await postToRails(
              "places",
              { query, keyplaces: keyplaces || !query },
              { returnData: false }
            )
          : { data: [], key_places: [] };
      // console.log({ data })
      const models = {
        data: data.map((p) => new Place(p)),
        key_places: key_places.map((p) => new Place(p)),
      };
      if (models.key_places.length && !keyPlaces.length)
        keyPlaces = [...models.key_places];
      if (!models.key_places.length && keyPlaces.length)
        models.key_places = [...keyPlaces];
      return models;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },
  findLocations: async function (term) {
    const { data: places } = await this.fetchLocations(term);
    places.forEach((place) => {
      locationCache[place.id] = place;
    });
    return places;
  },
  findOptions: async function (term) {
    // console.log({ term })
    const places = await this.findLocations(term);

    // console.log({ places })
    const filtered = places.map((location) => {
      return {
        label: location.n || location.name,
        value: location.id,
      };
    });
    // console.log({ filtered })
    return filtered;
  },
  find: async function (id) {
    if (locationCache[id.toString()]) return locationCache[id];
    if (inFlight[id]) {
      const reply = new Promise((resolve) => {
        inFlight[id].push(resolve);
      });
      return reply;
    } else {
      inFlight[id] = [];
    }
    const { data } = await this.fetchLocations(id);
    // console.log({ data })
    const location = data.find((t) => {
      return t.id.toString() === id.toString();
    });
    if (location) locationCache[location.id.toString()] = location;
    while (inFlight[id].length) {
      inFlight[id].shift()(location);
    }
    delete inFlight[id];
    return location;
  },
  findTier: async function (lat, lng) {
    try {
      const parents = await postToRails("places/tier", { lat, lng });
      for (let p = 0; p < parents.length; p++) {
        if (parents[p].tier) return parents[p].tier;
      }
      return Object.values(Tiers).shift();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },
  parents: async function (id) {
    try {
      if (parentCache[id.toString()]) return parentCache[id];
      const response = id
        ? await postToRails("places/parents", { place_id: id })
        : [];
      parentCache[id.toString()] = response;
      return response;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },
  locationLabel: async function (id) {
    const location = await this.find(id);
    return location ? location.name : "";
  },
  getFromIp: async function () {
    if (typeof window === "undefined") return null;

    return new Promise((resolve, reject) => {
      try {
        Geo.locationFromIp().then((locations) => {
          if (locations.length) {
            const townCity = locations.find((l) =>
              ["city", "town"].includes(l.kind.toLowerCase())
            );
            if (townCity) {
              resolve(townCity);
            } else {
              resolve(locations[0]);
            }
          } else {
            reject(new Error("None discovered"));
          }
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    });
  },
  currentPosition: async function () {
    return new Promise((resolve, reject) => {
      if (!navigator || !navigator.geolocation)
        return reject(new Error("Browser cannot detect location"));
      navigator.geolocation.getCurrentPosition((position) => {
        Geo.locationFromLatLng({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }).then((locations) => {
          if (locations.length) {
            locations = locations.map((location) => new Place({ location }));
            const townCity = locations.find((l) =>
              ["city", "town"].includes(l.kind.toLowerCase())
            );
            // console.log(townCity)
            authenticationService.updatePlace(townCity || locations[0]);
            resolve(townCity || locations[0]);
          } else {
            reject(new Error("None discovered"));
          }
        });
      });
    });
  },
  autoCurrentPosition: async function () {
    return new Promise(async (resolve, reject) => {
      if (!navigator || !navigator.permissions) return resolve(); // reject('Browser cannot determine the location permission')

      navigator.permissions
        .query({ name: "geolocation" })
        .then(async (permission) => {
          if (permission && permission.state === "granted") {
            try {
              resolve(await this.currentPosition());
            } catch (e) {
              reject(e);
            }
          } else {
            reject(new Error("Permission not granted"));
          }
        });
    });
  },
  getResults:
    (base, _filter = {}) =>
    async (query) => {
      if (isSSR()) return [];
      if (controller) controller.abort();
      const countryCode =
        window && window.country && window.country.code
          ? window.country.code
          : "GB";
      if (!getResultsCache[countryCode]) getResultsCache[countryCode] = {};

      if (!getResultsCache[countryCode][query]) {
        getResultsCache[countryCode][query] = await locations.fetchLocations(
          query
        );
      }

      const { data, key_places: keyPlaces } =
        getResultsCache[countryCode][query];
      if (data.length < 1) {
        window.dataLayer.push({
          event: "autocomplete_location",
          value: query,
        });
      }

      const sortedResults = data.reduce(
        (acc, location) => {
          let category = "points_of_interest";

          if (location.kind === "venue") {
            category = "venue";
          } else if (cityKinds.indexOf(location.kind) >= 0) {
            category = "city";
          } else if (location.kind === "postcode") {
            category = "postcode";
          }

          if (acc[category].length < 5) acc[category].push(location);

          return acc;
        },
        {
          city: [],
          points_of_interest: [],
          postcode: [],
          venue: [],
          key_places: [],
        }
      );
      sortedResults.key_places = [...keyPlaces];
      const results = Object.entries(sortedResults)
        .map(([kind, results]) => {
          if (results.length === 0) return null;
          return {
            label: kind.replace(/_/g, " "),
            options: results.map((place) => ({
              label: place.title || place.name,
              kind: place.kind,
              coordinates: { latitude: place.lat, longitude: place.lng },
              value:
                place.kind === "venue"
                  ? `/book-venue-${place.citySlug}/venue-${place.slug}`
                  : place.slug,
            })),
          };
        })
        .filter(Boolean);
      return results;
    },
};
